import { useCallback } from "react";

const hasAcknowledgedCareSource = (payload, childId) => {
  const capture = payload?.captureSource,
    note = payload?.sourceNote;
  if (
    capture?.sourceNoteCollection !== "careSources" &&
    note?.collection !== "careSources"
  )
    return false;
  const ids = [capture?.sourceNoteId, note?.id].filter(Boolean);
  if (
    !ids.length ||
    new Set(ids).size !== 1 ||
    (capture?.childId && capture.childId !== childId) ||
    (note?.childId && note.childId !== childId)
  )
    throw new Error(
      "The acknowledged capture does not match this care profile. Refresh before continuing.",
    );
  return true;
};

// Completes the dashboard handoff from QuickCheckIn's review flow.
export default function useHeadlessQuickNoteConvert({
  composerSubmission,
  captureTextAsBackgroundSource,
  dashboardSelectedChild,
  hook,
  setQuickComposerSavingMode,
  setQuickComposerSaving,
  setCareToastMessage,
  setCareToastSeverity,
  setCareToastOpen,
}) {
  const { complete, cancel } = composerSubmission;
  return useCallback(
    async (payload) => {
      const cleanText = String(payload?.text || "").trim();

      if (cleanText) {
        const child =
          payload?.child || hook.selectedChild || dashboardSelectedChild;
        try {
          // QuickCheckIn already saved this source and started its processor.
          // Completing the handoff must not create another original.
          if (hasAcknowledgedCareSource(payload, child?.id)) {
            setQuickComposerSaving(false);
            hook.handleQuickEntrySkip();
            complete();
            return;
          }
        } catch (error) {
          setQuickComposerSaving(false);
          cancel();
          setCareToastMessage(error.message);
          setCareToastSeverity("error");
          setCareToastOpen(true);
          return;
        }
        setQuickComposerSavingMode("source");
        setQuickComposerSaving(true);
        const savedSource = await captureTextAsBackgroundSource({
          child,
          text: cleanText,
          captureDate: payload?.defaultDate || hook.quickEntryDate || null,
          inputMode: "quick_check_in",
          sourceName: "Typed note",
        });
        setQuickComposerSaving(false);
        hook.handleQuickEntrySkip();
        if (savedSource) {
          complete();
          return;
        }

        cancel();
        setCareToastMessage(
          "We saved your note, but couldn't start background processing.",
        );
        setCareToastSeverity("error");
        setCareToastOpen(true);
        return;
      }

      setQuickComposerSaving(false);
      cancel();
      hook.handleQuickEntrySkip();
    },
    [
      complete,
      cancel,
      captureTextAsBackgroundSource,
      dashboardSelectedChild,
      hook,
      setQuickComposerSavingMode,
      setQuickComposerSaving,
      setCareToastMessage,
      setCareToastSeverity,
      setCareToastOpen,
    ],
  );
}
